import React, { useEffect, useState } from "react";
import AppLayout from "@/components/layout/AppLayout";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Building2, Factory, ShieldCheck, Wrench, Zap, Flame, Download } from "lucide-react";
import { LineChart, Line, BarChart, Bar, AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const HERO = "https://images.unsplash.com/photo-1768128834406-f5b1b962af9d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwzfHxpbmR1c3RyaWFsJTIwcGxhbnQlMjBlbmdpbmVlcnxlbnwwfHx8fDE3ODI1NDQzOTN8MA&ixlib=rb-4.1.0&q=85";

function Tile({ icon: Icon, label, value, unit }) {
  return (
    <Card className="p-4">
      <div className="flex items-center justify-between">
        <div>
          <div className="label-cap text-slate-500">{label}</div>
          <div className="text-2xl font-light mt-1" style={{ fontFamily: "Chivo" }}>{value} {unit && <span className="text-sm text-slate-500">{unit}</span>}</div>
        </div>
        <Icon className="h-5 w-5 text-sky-700" />
      </div>
    </Card>
  );
}

export default function CustomerPortal() {
  const { user } = useAuth();
  const [project, setProject] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [trend, setTrend] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProject(r.data[0]));
  }, []);

  useEffect(() => {
    if (!project) return;
    const p = { project_id: project.id };
    api.get("/dashboard/kpis", { params: p }).then((r) => setKpi(r.data));
    api.get("/dashboard/trends", { params: { ...p, days: 30 } }).then((r) => setTrend(r.data));
  }, [project]);

  const download = (kind) => {
    const t = localStorage.getItem("neipl_token");
    const url = `${API}/reports/production/${kind}?project_id=${project.id}`;
    fetch(url, { headers: { Authorization: `Bearer ${t}` } })
      .then((r) => r.blob())
      .then((b) => {
        const u = URL.createObjectURL(b);
        const a = document.createElement("a");
        a.href = u; a.download = `${project.name.replace(/\s/g, "_")}_report.${kind === "excel" ? "xlsx" : "pdf"}`;
        a.click();
      });
  };

  if (!project) return <AppLayout title="Customer Portal" subtitle="">Loading…</AppLayout>;

  return (
    <AppLayout title="Customer Portal" subtitle={`${project.name} · ${project.client}`}>
      {/* Hero banner */}
      <Card className="overflow-hidden mb-6 border-0">
        <div className="relative h-44" style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/85 to-slate-900/40" />
          <div className="relative h-full flex items-center px-8">
            <div className="text-white">
              <div className="label-cap text-sky-300/80">Read-only access</div>
              <h2 className="text-3xl font-bold tracking-tight mt-1" style={{ fontFamily: "Chivo" }}>{project.name}</h2>
              <div className="text-slate-300 text-sm mt-1">{project.location} · {project.capacity_tpd} TPD design capacity</div>
            </div>
          </div>
        </div>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Tile icon={Factory} label="Feedstock Received (Today)" value={kpi?.feedstock_received_today ?? "—"} unit="MT" />
        <Tile icon={Factory} label="Feedstock Processed (Today)" value={kpi?.feedstock_processed_today ?? "—"} unit="MT" />
        <Tile icon={Building2} label="Feedstock Yard Stock" value={(kpi?.feedstock_yard_stock ?? 0).toLocaleString("en-IN")} unit="MT" />
        <Tile icon={Factory} label="AFR Produced (Today)" value={kpi?.today_production ?? "—"} unit="MT" />
        <Tile icon={Flame} label="Energy Delivered (MTD)" value={(kpi?.energy_delivered_mtd ?? 0).toLocaleString("en-IN")} unit="MKcal" />
        <Tile icon={Flame} label="Fuel / MT (MTD)" value={kpi?.fuel_per_mt_month ?? "—"} unit="L/MT" />
        <Tile icon={Zap} label="Electricity / MT (MTD)" value={kpi?.electricity_per_mt_month ?? "—"} unit="kWh/MT" />
        <Tile icon={ShieldCheck} label="Wtd Avg NCV-AFR" value={kpi?.weighted_ncv_afr ?? "—"} unit="Kcal/kg" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="label-cap text-slate-500">Production Trend</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Feedstock Received vs AFR Produced (MT)</div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={trend}>
              <defs>
                <linearGradient id="g-cust" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#0EA5E9" stopOpacity={0.4} />
                  <stop offset="100%" stopColor="#0EA5E9" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Area dataKey="feedstock" stroke="#475569" fillOpacity={0} name="Feedstock Received (MT)" />
              <Area dataKey="afr" stroke="#0EA5E9" fill="url(#g-cust)" name="AFR Produced (MT)" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-5">
          <div className="label-cap text-slate-500">NCV & Moisture</div>
          <div className="text-lg font-semibold mb-4" style={{ fontFamily: "Chivo" }}>Quality Parameters</div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={(d) => d?.slice(5)} />
              <YAxis yAxisId="l" tick={{ fontSize: 11 }} /><YAxis yAxisId="r" orientation="right" tick={{ fontSize: 11 }} />
              <Tooltip /><Legend wrapperStyle={{ fontSize: 12 }} />
              <Line yAxisId="l" dataKey="ncv" stroke="#0C4A6E" name="NCV" dot={false} />
              <Line yAxisId="r" dataKey="moisture" stroke="#D97706" name="Moist %" dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <div className="label-cap text-slate-500">Downloads</div>
            <div className="text-lg font-semibold" style={{ fontFamily: "Chivo" }}>Monthly Reports</div>
            <p className="text-sm text-slate-500 mt-1">PDF and Excel reports for your project — generated on demand.</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => download("pdf")} className="bg-sky-900 hover:bg-sky-950" data-testid="customer-download-pdf">
              <Download className="h-3.5 w-3.5 mr-1" /> PDF Report
            </Button>
            <Button variant="outline" onClick={() => download("excel")} data-testid="customer-download-excel">
              <Download className="h-3.5 w-3.5 mr-1" /> Excel
            </Button>
          </div>
        </div>
      </Card>
    </AppLayout>
  );
}
